import type { ComponentProps } from "react";
import type { UseFormReturn } from "react-hook-form";
import { t } from "ttag";

import type { ContactInfoFormValues } from "@/features/onboarding/hooks";
import { useAddressAutocomplete } from "@/features/onboarding/hooks/use-address-autocomplete";
import { Form, HomeIcon, Input, useToastStore } from "@/shared/ui";

type AddressType = "billing" | "shipping";

interface AddressAutocompleteInputProps {
  form: UseFormReturn<ContactInfoFormValues>;
  type: AddressType;
}

export const AddressAutocompleteInput = ({
  form,
  type,
}: AddressAutocompleteInputProps) => {
  const { suggestions, hasSuggestions, onInputChange, onDismiss, onSelect } =
    useAddressAutocomplete();
  const pushToast = useToastStore((state) => state.pushToast);

  const { onChange, onBlur, ...rest } = form.register(`${type}.address`);

  const handleChange: ComponentProps<"input">["onChange"] = (e) => {
    void onChange(e);
    onInputChange(e.target.value);
  };

  const handleBlur: ComponentProps<"input">["onBlur"] = (e) => {
    void onBlur(e);
    onDismiss();
  };

  const handleKeyDown: ComponentProps<"input">["onKeyDown"] = (e) => {
    if (e.key === "Escape") {
      onDismiss();
    }
  };

  const handleSelect = async (placeId: string) => {
    try {
      const address = await onSelect(placeId);

      const options = { shouldValidate: true, shouldDirty: true };

      form.setValue(`${type}.address`, address.address, options);
      form.setValue(`${type}.city`, address.city, options);
      form.setValue(`${type}.zipCode`, address.zipCode, options);

      if (type === "shipping" && address.state) {
        form.setValue("state", address.state, options);
      }
    } catch {
      void pushToast({
        type: "error",
        title: t`Error`,
        message: t`Couldn't load this address. Please enter it manually.`,
      });
    }
  };

  return (
    <Form.Field
      control={form.control}
      name={`${type}.address`}
      render={() => (
        <Form.Item
          id={`${type}-address`}
          className="mf-masked mf-listen relative sm:col-span-2"
        >
          <Form.Label aria-required>{t`Address`}</Form.Label>
          <Form.Control>
            <Input.Root
              {...rest}
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              placeholder={t`123, Main Street`}
              autoComplete="off"
            >
              <Input.Slot>
                <HomeIcon />
              </Input.Slot>
            </Input.Root>
          </Form.Control>
          {hasSuggestions && (
            <ul
              role="listbox"
              className="absolute top-full right-0 left-0 z-50 mt-1 max-h-60 overflow-auto rounded-lg border border-neutral-300 bg-white shadow-lg"
            >
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.place_id}
                  role="option"
                  aria-selected={false}
                  className="cursor-pointer px-4 py-2.5 text-sm text-neutral-900 hover:bg-secondary-50"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    void handleSelect(suggestion.place_id);
                  }}
                >
                  {suggestion.description}
                </li>
              ))}
            </ul>
          )}
          <Form.Message
            compact={false}
            className="absolute inset-x-0 top-full mt-1"
          />
        </Form.Item>
      )}
    />
  );
};
